import { Button } from '@civilgrid/ui/components';
import { useMutation } from '@tanstack/react-query';
import type { FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Layer, Source } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import {
  type AgencySubGeometry,
  createAgencySubGeometry as apiCreate,
  updateAgencySubGeometry as apiUpdate,
} from '~/api/civilsearch';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { MapStyle } from '~/common/types';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import Form from '~/components2/forms/Form';
import { MapComponent } from '~/components2/Map';
import { Notify } from '~/components2/Notify';
import EditorSurface from './draw/EditorSurface';
import { computeDrawInitialViewState, nextDefaultSubGeometryName } from './draw/helpers';
import Surface from './draw/Surface';
import drawStore from './drawStore';
import { buildSubGeometryFeatureCollection } from './helpers';
import { computeContainedFraction, findOverlappingSubGeometryIds, isOutOfBounds } from './validation';

const FOOTPRINT_SOURCE_ID = 'draw-agency-footprint-source';
const EXISTING_SOURCE_ID = 'draw-existing-sub-geometries-source';

type FormValues = { name: string };

export default function SubGeometryDrawView({
  agencyId,
  footprint,
  existingSubGeometries,
  subGeometry,
  onDone,
}: {
  agencyId: string;
  footprint: FeatureCollection;
  existingSubGeometries: AgencySubGeometry[];
  subGeometry?: AgencySubGeometry;
  onDone: () => void;
}) {
  const isEdit = Boolean(subGeometry);
  const snap = useSnapshot(drawStore) as typeof drawStore;
  // Don't read the objectsAsArray getter off the snapshot: valtio snapshots copy only own keys, so
  // the prototype getter re-runs on every access and returns a fresh array each render. Depend on
  // the objects subtree instead — snapshots are structurally shared, so its identity only changes
  // when a drawn object actually changes.
  const objectsSnapshot = snap.currentMap.isLoading ? undefined : snap.currentMap.objects;
  const objectsAsArray = useMemo(() => Object.values(objectsSnapshot ?? {}), [objectsSnapshot]);
  // Edit: prefill the existing name. Create: prefill an editable default ("Region N").
  const form = useForm<FormValues>({
    defaultValues: {
      name: subGeometry
        ? (subGeometry.name ?? '')
        : nextDefaultSubGeometryName(existingSubGeometries.map((sg) => sg.name)),
    },
  });

  // When editing, the sub-geometry being edited becomes an editable draw object, so drop it from
  // the read-only "existing" reference layer to avoid drawing it twice.
  const otherSubGeometries = useMemo(
    () => existingSubGeometries.filter((sg) => sg.id !== subGeometry?.id),
    [existingSubGeometries, subGeometry],
  );
  const existingFC = useMemo(() => buildSubGeometryFeatureCollection(otherSubGeometries), [otherSubGeometries]);
  // Edit: frame on the sub-geometry being edited. Create: frame on footprint + existing.
  const initialViewState = useMemo(
    () =>
      subGeometry
        ? computeDrawInitialViewState(buildSubGeometryFeatureCollection([subGeometry]))
        : computeDrawInitialViewState(footprint, existingFC),
    [subGeometry, footprint, existingFC],
  );

  useEffect(() => {
    drawStore.reset();
    if (subGeometry) {
      drawStore.loadGeometry(subGeometry.geometry as Polygon | MultiPolygon);
      capturePosthogEvent(POSTHOG_EVENTS.civilsearchAgencySubGeometryEditStarted, {
        agency_id: agencyId,
        sub_geometry_id: subGeometry.id,
      });
    } else {
      drawStore.startDraw();
      capturePosthogEvent(POSTHOG_EVENTS.civilsearchAgencySubGeometryDrawStarted, { agency_id: agencyId });
    }
    return () => drawStore.reset();
  }, [agencyId, subGeometry]);

  // The in-progress geometry recomputes whenever the drawn objects change (objectsSnapshot is the
  // reactive trigger; geometryAsMultiPolygon reads the live store).
  // biome-ignore lint/correctness/useExhaustiveDependencies: objectsSnapshot is the reactive trigger; drawStore is a stable module singleton.
  const drawnGeometry = useMemo<MultiPolygon | null>(() => drawStore.geometryAsMultiPolygon ?? null, [objectsSnapshot]);

  // CG-482 client-side validation — fast feedback before the request; the API re-checks and stays
  // authoritative. Overlap blocks the save; out-of-bounds is warn-only.
  const conflictIds = useMemo(
    () => (drawnGeometry ? findOverlappingSubGeometryIds(drawnGeometry, otherSubGeometries) : []),
    [drawnGeometry, otherSubGeometries],
  );
  const containedFraction = useMemo(
    () => (drawnGeometry ? computeContainedFraction(drawnGeometry, footprint) : null),
    [drawnGeometry, footprint],
  );
  const outOfBounds = isOutOfBounds(containedFraction);

  // Overlap ids reported by the server's 409 — a backstop for anything the client check misses.
  // Cleared whenever the geometry changes so a stale conflict doesn't linger after a re-draw.
  const [serverOverlapIds, setServerOverlapIds] = useState<string[]>([]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: drawnGeometry is the intentional reset trigger — clear the stale server overlap whenever the draw changes.
  useEffect(() => {
    setServerOverlapIds((prev) => (prev.length ? [] : prev));
  }, [drawnGeometry]);

  const conflictHighlightIds = useMemo(
    () => Array.from(new Set([...conflictIds, ...serverOverlapIds])),
    [conflictIds, serverOverlapIds],
  );
  const hasOverlap = conflictHighlightIds.length > 0;

  // The Save button is disabled while a client-detected overlap is present, so this is the moment
  // the save is actually blocked — fire it once per conflict episode (reset when the overlap clears).
  const overlapReportedRef = useRef(false);
  useEffect(() => {
    if (conflictIds.length === 0) {
      overlapReportedRef.current = false;
      return;
    }
    if (overlapReportedRef.current) return;
    overlapReportedRef.current = true;
    capturePosthogEvent(POSTHOG_EVENTS.civilsearchAgencySubGeometryOverlapBlocked, {
      agency_id: agencyId,
      ...(subGeometry ? { sub_geometry_id: subGeometry.id } : {}),
      overlapping_ids: conflictIds,
    });
  }, [conflictIds, agencyId, subGeometry]);

  const mutation = useMutation({
    mutationFn: async (name: string) => {
      const geometry = drawStore.geometryAsMultiPolygon;
      if (!geometry) throw new Error('Draw at least one polygon before saving');
      // Strip the non-standard `properties` member geomToMultiPolygon adds; send a clean GeoJSON geometry.
      const body = { name, geometry: { type: geometry.type, coordinates: geometry.coordinates } };
      if (subGeometry) return apiUpdate(agencyId, subGeometry.id, body);
      return apiCreate(agencyId, body);
    },
    onSuccess: (res) => {
      capturePosthogEvent(
        subGeometry
          ? POSTHOG_EVENTS.civilsearchAgencySubGeometryUpdated
          : POSTHOG_EVENTS.civilsearchAgencySubGeometryCreated,
        { agency_id: agencyId, sub_geometry_id: res.subGeometry.id },
      );
      // Warn-only out-of-bounds: the save succeeded; surface the server's containment warning but
      // never block on it (CG-482 / CG-473 invariant 3).
      if (res.warning?.code === 'containment') {
        capturePosthogEvent(POSTHOG_EVENTS.civilsearchAgencySubGeometryOutOfBoundsWarned, {
          agency_id: agencyId,
          sub_geometry_id: res.subGeometry.id,
          contained_fraction: res.warning.containedFraction,
        });
        Notify.warning({
          message: `Sub region ${subGeometry ? 'updated' : 'created'}. Heads up: only about ${Math.round(
            res.warning.containedFraction * 100,
          )}% of it falls inside the agency's service area — part of it is outside.`,
        });
      } else {
        Notify.info({ message: subGeometry ? 'Sub region updated' : 'Sub region created' });
      }
      drawStore.reset();
      onDone();
    },
    onError: (e) => {
      // CG-482: the API returns 409 with the offending ids when the geometry overlaps a sibling.
      // Surface the server's reason and highlight the conflict on the map.
      const response = (e as { response?: { status?: number; data?: { reason?: string; overlappingIds?: string[] } } })
        ?.response;
      if (response?.status === 409) {
        const ids = response.data?.overlappingIds ?? [];
        setServerOverlapIds(ids);
        capturePosthogEvent(POSTHOG_EVENTS.civilsearchAgencySubGeometryOverlapBlocked, {
          agency_id: agencyId,
          ...(subGeometry ? { sub_geometry_id: subGeometry.id } : {}),
          overlapping_ids: ids,
        });
        Notify.error({
          message: response.data?.reason ?? 'This area overlaps an existing sub region for this agency.',
        });
        return;
      }
      console.error('Error saving sub region', e);
      Notify.error({ message: subGeometry ? 'Error updating sub region' : 'Error creating sub region' });
    },
  });

  const hasGeometry = objectsAsArray.length > 0;
  // Enforce a single polygon per sub region — block the save (and warn) if more than one was drawn.
  const tooManyPolygons = objectsAsArray.length > 1;
  const nameValue = form.watch('name');
  // Defensive: Save is disabled while an overlap or extra polygon is present, but never submit a
  // known-invalid draw even if the button were somehow reachable — the API would reject it.
  const onSubmit: SubmitHandler<FormValues> = ({ name }) => {
    if (hasOverlap || tooManyPolygons) return;
    mutation.mutate(name);
  };

  return (
    <div className="relative h-full w-full" data-testid="agency-sub-geometries-draw-view">
      <MapComponent
        id="agency-sub-geometry-draw-map"
        store={drawStore}
        // Pin to the same light streets basemap the list screen uses so the footprint always reads.
        // This is render-only and does not modify the user's saved basemap preference (rootStore.preferredBasemap).
        mapStyle={MapStyle.streets}
        showLayers={false}
        showLayerPicker={false}
        showLegend={false}
        showBasemapPicker={false}
        showZoomButtons={true}
        showScale={true}
        measureTool={false}
        printTool={false}
        initialViewState={initialViewState}
      >
        {footprint.features.length > 0 && (
          <Source id={FOOTPRINT_SOURCE_ID} type="geojson" data={footprint}>
            <Layer
              id="draw-agency-footprint-fill"
              source={FOOTPRINT_SOURCE_ID}
              type="fill"
              paint={{ 'fill-color': '#6b7280', 'fill-opacity': 0.08 }}
            />
            <Layer
              id="draw-agency-footprint-line"
              source={FOOTPRINT_SOURCE_ID}
              type="line"
              paint={{ 'line-color': '#6b7280', 'line-width': 1, 'line-dasharray': [2, 2] }}
            />
          </Source>
        )}
        {existingFC.features.length > 0 && (
          <Source id={EXISTING_SOURCE_ID} type="geojson" data={existingFC}>
            <Layer
              id="draw-existing-sub-geometries-fill"
              source={EXISTING_SOURCE_ID}
              type="fill"
              paint={{ 'fill-color': '#2563eb', 'fill-opacity': 0.12 }}
            />
            <Layer
              id="draw-existing-sub-geometries-line"
              source={EXISTING_SOURCE_ID}
              type="line"
              paint={{ 'line-color': '#2563eb', 'line-width': 1.5 }}
            />
            {/* CG-482: highlight the sub-geometries the current draw conflicts with, in red. */}
            <Layer
              id="draw-existing-sub-geometries-conflict-fill"
              source={EXISTING_SOURCE_ID}
              type="fill"
              filter={['in', ['get', 'id'], ['literal', conflictHighlightIds]]}
              paint={{ 'fill-color': '#ef4444', 'fill-opacity': 0.4 }}
            />
            <Layer
              id="draw-existing-sub-geometries-conflict-line"
              source={EXISTING_SOURCE_ID}
              type="line"
              filter={['in', ['get', 'id'], ['literal', conflictHighlightIds]]}
              paint={{ 'line-color': '#dc2626', 'line-width': 3 }}
            />
          </Source>
        )}

        <Surface />
        <EditorSurface />

        <div
          className="absolute left-2 top-2 z-10 w-72 rounded bg-white p-4 shadow"
          data-testid="agency-sub-geometries-draw-panel"
        >
          <Form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="text-lg text-secondary">{isEdit ? 'Edit sub region' : 'New sub region'}</div>
            <p className="my-2 text-xs text-grey-60">
              {isEdit
                ? 'Click the polygon to adjust its vertices.'
                : 'Click on the map to begin drawing; double-click or press Enter to finish a polygon.'}
            </p>
            <ControlledInput
              control={form.control}
              name="name"
              label="Name"
              isRequired
              data-testid="agency-sub-geometries-draw-name-input"
            />
            <div className="mt-3 flex gap-2">
              <Button
                type="submit"
                disabled={!hasGeometry || tooManyPolygons || hasOverlap || !nameValue?.trim() || mutation.isPending}
                data-testid="agency-sub-geometries-draw-save"
              >
                {mutation.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  drawStore.reset();
                  onDone();
                }}
                data-testid="agency-sub-geometries-draw-cancel"
              >
                Cancel
              </Button>
            </div>
            {!hasGeometry && <p className="mt-2 text-2xs text-grey-60">Draw at least one polygon to enable Save.</p>}
            {tooManyPolygons && (
              <p
                className="mt-2 rounded bg-red-10 p-2 text-2xs text-red-60"
                data-testid="agency-sub-geometries-too-many-polygons"
              >
                A sub region can have only one polygon. Remove the extra area before saving.
              </p>
            )}
            {hasOverlap && (
              <p
                className="mt-2 rounded bg-red-10 p-2 text-2xs text-red-60"
                data-testid="agency-sub-geometries-overlap-error"
              >
                This area overlaps an existing sub region (highlighted in red). Sub regions can’t overlap — adjust the
                boundary so it no longer covers an existing one, then save.
              </p>
            )}
            {!hasOverlap && outOfBounds && containedFraction !== null && (
              <p
                className="mt-2 rounded bg-amber-50 p-2 text-2xs text-amber-700"
                data-testid="agency-sub-geometries-out-of-bounds-warning"
              >
                Heads up: only about {Math.round(containedFraction * 100)}% of this area falls inside the agency’s
                service area. You can still save it.
              </p>
            )}
          </Form>
        </div>
      </MapComponent>
    </div>
  );
}
